import React, { useState, useEffect, useRef } from 'react';
import Lottie from 'react-lottie';
import ERPSFilter from './ERPSFilter';
import coinAnimationData from '../../assets/animation/coin.json';
import rewardAnimationData from '../../assets/animation/reward.json';

// ─── Timing ──────────────────────────────────────────────────────────────────
const VISIBLE_MS = 4500;
const FADE_MS    = 1000;

// ─── Lottie options (outside component = no remount / no glitch) ─────────────
const coinOptions = {
  loop: true,
  autoplay: true,
  animationData: coinAnimationData,
  rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
};

const rewardOptions = {
  loop: false,      // ← plays once only
  autoplay: true,
  animationData: rewardAnimationData,
  rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
};

// ─── Inline CSS injected once ─────────────────────────────────────────────────
const STYLES = `
  @keyframes erps-glow-ring {
    0%, 100% { box-shadow: 0 0 40px 10px rgba(253,185,19,0.25), 0 0 80px 20px rgba(253,185,19,0.10); }
    50%       { box-shadow: 0 0 60px 18px rgba(253,185,19,0.45), 0 0 120px 40px rgba(253,185,19,0.18); }
  }
  @keyframes erps-shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes erps-float {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-8px); }
  }
  @keyframes erps-fade-in-up {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes erps-dots {
    0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
    40%            { opacity: 1;   transform: scale(1); }
  }
  .erps-dot:nth-child(1) { animation-delay: 0s;    }
  .erps-dot:nth-child(2) { animation-delay: 0.18s; }
  .erps-dot:nth-child(3) { animation-delay: 0.36s; }
`;

const EmployeeRewardsPage = () => {
  const [phase, setPhase] = useState('visible'); // 'visible' | 'fading' | 'done'
  const fadeTimer   = useRef(null);
  const removeTimer = useRef(null);
  const styleInjected = useRef(false);

  useEffect(() => {
    // Inject keyframes once
    if (!styleInjected.current) {
      const tag = document.createElement('style');
      tag.textContent = STYLES;
      document.head.appendChild(tag);
      styleInjected.current = true;
    }

    fadeTimer.current   = setTimeout(() => setPhase('fading'), VISIBLE_MS);
    removeTimer.current = setTimeout(() => setPhase('done'),   VISIBLE_MS + FADE_MS);

    return () => {
      clearTimeout(fadeTimer.current);
      clearTimeout(removeTimer.current);
    };
  }, []);

  if (phase === 'done') return <ERPSFilter />;

  const isFading = phase === 'fading';

  return (
    <>
      {/* ── Blurred Intro Overlay ───────────────────────────────────────────── */}
      <div
        style={{
          position:       'fixed',
          inset:          0,
          zIndex:         9999,
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          background:     'rgba(8, 18, 35, 0.52)',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          opacity:        isFading ? 0 : 1,
          transition:     `opacity ${FADE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          pointerEvents:  'none',
        }}
      >

        {/* ── Coin shower — BEHIND (z 1) ─────────────────────────────────── */}
        <div
          style={{
            position: 'absolute',
            inset:    0,
            zIndex:   1,
            opacity:  0.45,
          }}
        >
          <Lottie
            options={coinOptions}
            height="100%"
            width="100%"
            isClickToPauseDisabled={true}
          />
        </div>

        {/* ── Glassmorphism card + Reward — IN FRONT (z 2) ──────────────── */}
        <div
          style={{
            position:        'relative',
            zIndex:          2,
            display:         'flex',
            flexDirection:   'column',
            alignItems:      'center',
            gap:             '18px',
            padding:         '40px 48px 32px',
            borderRadius:    '28px',
            background:      'rgba(255, 255, 255, 0.07)',
            border:          '1px solid rgba(253, 185, 19, 0.30)',
            animation:       'erps-glow-ring 2.4s ease-in-out infinite, erps-fade-in-up 0.7s ease-out both',
          }}
        >
          {/* Reward Lottie — slowed down, plays once */}
          <div style={{ animation: 'erps-float 3s ease-in-out infinite' }}>
            <Lottie
              options={rewardOptions}
              speed={0.55}               /* ← slowed to ~55 % of original speed */
              height={360}
              width={360}
              isClickToPauseDisabled={true}
            />
          </div>

          {/* Title */}
          <div
            style={{
              fontFamily:      "'Segoe UI', Inter, sans-serif",
              fontWeight:      800,
              fontSize:        '22px',
              letterSpacing:   '2.5px',
              textTransform:   'uppercase',
              background:      'linear-gradient(90deg, #fdb913, #fff8dc, #fdb913)',
              backgroundSize:  '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation:       'erps-shimmer 2.2s linear infinite, erps-fade-in-up 0.9s ease-out 0.2s both',
            }}
          >
            ✨ Employee Reward Points ✨
          </div>

          {/* Subtitle */}
          <div
            style={{
              color:         'rgba(255,255,255,0.55)',
              fontSize:      '13px',
              letterSpacing: '1.2px',
              display:       'flex',
              alignItems:    'center',
              gap:           '6px',
              animation:     'erps-fade-in-up 1s ease-out 0.4s both',
            }}
          >
            Loading your rewards
            {['•','•','•'].map((d, i) => (
              <span
                key={i}
                className="erps-dot"
                style={{
                  display:         'inline-block',
                  width:           '5px',
                  height:          '5px',
                  borderRadius:    '50%',
                  background:      '#fdb913',
                  animation:       'erps-dots 1.2s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        </div>

      </div>

      {/* ── Page renders underneath blur ─────────────────────────────────────── */}
      <ERPSFilter />
    </>
  );
};

export default EmployeeRewardsPage;